import { castArray, isNumeric, queryOnObject } from '../views/utils'
import { joinReferences } from './consts'
const { PARENT, CHILD, PIVOT } = joinReferences
const { fromEntries } = Object

const splitQueryDepth = (() => {
  const complexityDelimiter = '$'
  const depthDelimiter = '.'
  return (queryParams) => {
    if (!queryParams) return []
    if (isNumeric(queryParams)) return [queryParams]

    const simpleQueries = []
    const possiblyDeepQueries = []

    Object
      .keys(queryParams)
      .forEach((fieldName) => {
        // ignore $fetch to prevent infinite loops
        const isPossiblyDeep = fieldName[0] === complexityDelimiter ||
          fieldName.includes(depthDelimiter)

        ;(isPossiblyDeep ? possiblyDeepQueries : simpleQueries)
          .push([fieldName, queryParams[fieldName]])
      })

    return [simpleQueries, possiblyDeepQueries]
      .map(queryType => Object.fromEntries(queryType))
  }
})()

export default (storeModule) => {
  function parseJoins(joins) {
    if (!joins || !Object.keys(joins).length) return null

    const joinsEntries = Object.entries(joins)
    const directJoins = joinsEntries.filter(([_, join]) => join.referenceType === PARENT)
    const inverseDirectJoins = joinsEntries.filter(([_, join]) => join.referenceType === CHILD)
    const manyToManyJoins = joinsEntries.filter(([_, join]) => join.referenceType === PIVOT)
    return { directJoins, inverseDirectJoins, manyToManyJoins }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  return (_, { query, queryJoin }) => {
    return (queryParams, options = {}) => {
      const joins = parseJoins(options.join || storeModule.joins)
      if (!joins) {
        return query(storeModule.storeKey, queryParams, options)
      }

      const [simpleQuery, deepQuery] = splitQueryDepth(queryParams)

      // if (queryParams && queryParams.$fetch) {
      //   queryParams.$fetch.$store
      //     .dispatch(`FETCH_${storeModule.storeSuffix}`, buildServerQuery(queryParams))
      // }

      const isSingle = options.isSingle || isNumeric(queryParams)
      const shallowResults = query(storeModule.storeKey, simpleQuery, options)

      if (!shallowResults) return null

      const { directJoins, inverseDirectJoins, manyToManyJoins } = joins
      const joinedResults = castArray(shallowResults)
        .map((record) => {
          const recordDirectJoins = fromEntries(directJoins
            .map(([joinKey, join]) => {
              if (record[join.parentKey] === null) return [joinKey, null]

              // TODO: optimally allow to perform query when childKey is not 'id'
              return [
                joinKey,
                queryJoin(
                  join.storeKey,
                  record[join.parentKey],
                  { isSingle: true, join: join.children || null },
                ),
              ]
            }))

          const recordDirectInverseJoins = fromEntries(inverseDirectJoins
            .map(([joinKey, join]) => {
              return [
                joinKey,
                queryJoin(
                  join.storeKey,
                  { [join.childKey]: record[join.parentKey] },
                  { isSingle: !join.hasMany, join: join.children || null },
                ),
              ]
            }))

          const recordProxyJoins = fromEntries(manyToManyJoins
            .map(([joinKey, join]) => {
              const joinedIds = recordDirectInverseJoins[join.via]
                .map(relationshipRecord => relationshipRecord[join.viaChildKey])
                .filter(joinedId => joinedId)

              if (!joinedIds.length) return [joinKey, []]

              // TODO: allow specifying proxy store joins without other keys than just id
              return [
                joinKey,
                queryJoin(
                  join.storeKey,
                  { id: { $in: joinedIds } },
                  { isSingle: false, join: join.children || null },
                ),
              ]
            }))

          return {
            ...record,
            ...recordDirectJoins,
            ...recordDirectInverseJoins,
            ...recordProxyJoins,
          }
        })

      const filteredResults = (deepQuery && Object.keys(deepQuery).length)
        ? queryOnObject(
          joinedResults,
          storeModule.store.state,
        )(queryParams, options)
        : joinedResults

      return isSingle
        ? filteredResults[0] || null
        : filteredResults
    }

    // TODO: finish
    // const recordFields = Object.keys(entries[0][1])
    // const idFields = recordFields
    //   .filter(fieldName => fieldName.endsWith('Id'))

    // const results = Object
    //   .fromEntries(entries
    //     .map(([id, record]) => {
    //       const relatedObject = Object
    //         .defineProperties(
    //           { ...record },
    //           Object.fromEntries(idFields
    //             .map(idField => {
    //               const childName = idField.slice(0, -2)

    //               return [
    //                 childName,
    //                 {
    //                   get: () => queryShallow(childName, record[idField], true),
    //                 },
    //               ]
    //             })))

    //       return [id, relatedObject]
    //     }))

    // return results
  }
}
